<template>
    <div class="reatlas-building-list">
        <!-- Search Box -->
        <div class="reatlas-building-list__search">
            <themed-search
                v-model="searchQuery"
                placeholder="Search sites..."
                @input="handleSearchInput"
            />
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="reatlas-building-list__loading">
            <div class="reatlas-building-list__loading-spinner"></div>
            <p class="reatlas-building-list__loading-text">Loading sites...</p>
        </div>

        <!-- Sites View -->
        <div v-else-if="layout == 'site'" class="reatlas-building-list__container" id="site-card-container">
            <div 
                v-for="(marker, index) in sortedBuildings" 
                :key="marker.item.id" 
                @click="$emit('show-site', marker.item)" 
                class="reatlas-building-list__card"
                :class="{
                    'reatlas-building-list__card--selected': marker.item.id == selectedSite.id,
                    'reatlas-building-list__card--odd': marker.item.id != selectedSite.id && index % 2 === 0,
                    'reatlas-building-list__card--even': marker.item.id != selectedSite.id && index % 2 !== 0
                }"
            >
                <div class="reatlas-building-list__image-container">
                    <div v-if="marker.item.archived_at !== null" class="reatlas-building-list__archived-icon" title="Site is Archived">
                        <inline-svg name="archived"></inline-svg>
                    </div>
                    <img :src="marker.item.image?.view_url" class="reatlas-building-list__image">
                    <div 
                        class="reatlas-building-list__badge badge"
                        :class="marker.item.utilization_color"
                        title="Utilization"
                    >
                        {{ marker.item.utilization }}%
                    </div>
                </div>
                <div class="reatlas-building-list__content">
                    <div class="reatlas-building-list__info">
                        <p class="reatlas-building-list__address">
                            {{ marker.item.building?.address_line_1 | truncate(22) }}
                        </p>
                        <p class="reatlas-building-list__location">
                            {{ marker.item.building?.city }}, {{ marker.item.building?.state }}
                        </p>
                    </div>
                    <div class="reatlas-building-list__metrics">
                        <div class="reatlas-building-list__metric">
                            <p class="reatlas-building-list__metric-value is-currency" :data-originalvalue="marker.item.current_psf" data-precision="2">
                                {{ formatCurrency(marker.item.current_psf, 2, null, marker.item.currency) }}
                            </p>
                            <p class="reatlas-building-list__metric-label">Price PSF</p>
                        </div>
                        <div class="reatlas-building-list__metric">
                            <p class="reatlas-building-list__metric-value">{{ marker.item.size | number }}</p>
                            <p class="reatlas-building-list__metric-label">Site Size</p>
                        </div>
                    </div>
                </div>
            </div>
            
            <div 
                class="reatlas-building-list__scroll-indicator reatlas-building-list__scroll-indicator--up hidden"
                :style="{'top': scrollUpIndicatorPosition + 'px' }"
            >
                <inline-svg name="chevron-down" classes="w-4 h-4 text-white" />
            </div>
            <div 
                class="reatlas-building-list__scroll-indicator reatlas-building-list__scroll-indicator--down"
                :style="{'top': scrollDownIndicatorPosition + 'px' }"
            >
                <inline-svg name="chevron-down" classes="w-4 h-4 text-white" />
            </div>
        </div>

        <!-- Projects View -->
        <div v-else-if="layout == 'projects'" class="reatlas-building-list__container" id="comp-card-container">
            <div 
                v-for="(marker, index) in sortedBuildings" 
                :key="marker.item.id" 
                @click="$emit('show-comp', marker.item)" 
                class="reatlas-building-list__card"
                :class="{
                    'reatlas-building-list__card--selected': marker.item.id == selectedComp.building_id,
                    'reatlas-building-list__card--odd': marker.item.id != selectedComp.building_id && index % 2 === 0,
                    'reatlas-building-list__card--even': marker.item.id != selectedComp.building_id && index % 2 !== 0
                }"
            >
                <div class="reatlas-building-list__image-container">
                    <img :src="marker.item.image?.view_url" class="reatlas-building-list__image">
                    <div 
                        class="reatlas-building-list__badge badge"
                        :class="marker.item.pivot.score_color"
                        title="Score"
                    >
                        {{ marker.item.pivot.score }}
                    </div>
                </div>
                <div class="reatlas-building-list__content">
                    <div class="reatlas-building-list__info">
                        <p v-if="!isNegotiationMode" class="reatlas-building-list__address">
                            {{ marker.item.address_line_1 | truncate(22) }}
                        </p>
                        <p v-else class="reatlas-building-list__address">Alternative Site #{{ index + 1 }}</p>
                        <p class="reatlas-building-list__location">{{ marker.item.city }}, {{ marker.item.state }}</p>
                    </div>
                    <div class="reatlas-building-list__metrics">
                        <div class="reatlas-building-list__metric">
                            <p class="reatlas-building-list__metric-value is-currency" :data-originalvalue="marker.item.pivot.ask_price" data-precision="2">
                                {{ formatCurrency(marker.item.pivot.ask_price, 2, null, marker.item.currency) }}
                            </p>
                            <p class="reatlas-building-list__metric-label">Ask PSF</p>
                        </div>
                        <div class="reatlas-building-list__metric">
                            <p class="reatlas-building-list__metric-value">{{ marker.item.total_square_footage | number }}</p>
                            <p class="reatlas-building-list__metric-label">Comp Size</p>
                        </div>
                    </div>
                </div>
            </div>
            
            <div 
                class="reatlas-building-list__scroll-indicator reatlas-building-list__scroll-indicator--up hidden"
                :style="{'top': scrollUpIndicatorPosition + 'px' }"
            >
                <inline-svg name="chevron-down" classes="w-4 h-4 text-white" />
            </div>
            <div 
                class="reatlas-building-list__scroll-indicator reatlas-building-list__scroll-indicator--down"
                :style="{'top': scrollDownIndicatorPosition + 'px' }"
            >
                <inline-svg name="chevron-down" classes="w-4 h-4 text-white" />
            </div>
        </div>

        <div v-if="layout == 'projects' && selectedProjectId == ''" class="reatlas-building-list__no-project">
            Select a Project
        </div>

        <!-- Footer Stats -->
        <div v-if="layout == 'site'" class="reatlas-building-list__footer reatlas-building-list__footer--sites">
            <div class="reatlas-building-list__footer-stat">
                <p class="reatlas-building-list__footer-stat-value">{{ totalSites }}</p>
                <p class="reatlas-building-list__footer-stat-label">Total Sites</p>
            </div>
            <div class="reatlas-building-list__footer-stat">
                <p class="reatlas-building-list__footer-stat-value is-currency" :data-originalvalue="averagePsf" data-precision="2">
                    {{ formatCurrency(averagePsf, 2) }}
                </p>
                <p class="reatlas-building-list__footer-stat-label">Average PSF</p>
            </div>
        </div>

        <div v-if="layout == 'projects'" class="reatlas-building-list__footer reatlas-building-list__footer--projects">
            <div class="reatlas-building-list__footer-stat">
                <p class="reatlas-building-list__footer-stat-value">{{ totalComps }}</p>
                <p class="reatlas-building-list__footer-stat-label">Total Comps</p>
            </div>
            <div class="reatlas-building-list__footer-stat">
                <p class="reatlas-building-list__footer-stat-value is-currency" :data-originalvalue="averageAskPricePsf" data-precision="2">
                    {{ formatCurrency(averageAskPricePsf, 2) }}
                </p>
                <p class="reatlas-building-list__footer-stat-label">Average Ask PSF</p>
            </div>
        </div>
    </div>
</template>

<script>
import InlineSvg from '../../../InlineSvg';
import ThemedSearch from '../ThemedSearch.vue';

export default {
    name: 'ReatlasBuildingList',
    
    components: {
        InlineSvg,
        ThemedSearch
    },

    data() {
        return {
            searchQuery: ''
        };
    },

    methods: {
        handleSearchInput(value) {
            this.searchQuery = value;
            this.$emit('search', value);
        }
    },

    props: {
        layout: {
            type: String,
            required: true
        },
        sortedBuildings: {
            type: Array,
            required: true
        },
        selectedSite: {
            type: Object,
            default: () => ({})
        },
        selectedComp: {
            type: Object,
            default: () => ({})
        },
        totalSites: {
            type: Number,
            default: 0
        },
        totalComps: {
            type: Number,
            default: 0
        },
        averagePsf: {
            type: Number,
            default: 0
        },
        averageAskPricePsf: {
            type: Number,
            default: 0
        },
        selectedProjectId: {
            type: [String, Number],
            default: ''
        },
        scrollUpIndicatorPosition: {
            type: Number,
            default: 0
        },
        scrollDownIndicatorPosition: {
            type: Number,
            default: 0
        },
        isNegotiationMode: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        }
    }
}
</script>
